import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, TextField, Button, InputAdornment, IconButton } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import { styled } from '@mui/system';
import { motion } from 'framer-motion';
import { Tooltip } from '@mui/material';

// Import Material UI icons (feel free to add more if needed)
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import TvIcon from '@mui/icons-material/Tv';
import HomeIcon from '@mui/icons-material/Home';
import WeekendIcon from '@mui/icons-material/Weekend';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import FlightIcon from '@mui/icons-material/Flight';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import DiamondIcon from '@mui/icons-material/Diamond';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ComputerIcon from '@mui/icons-material/Computer';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import PetsIcon from '@mui/icons-material/Pets';
import ChildFriendlyIcon from '@mui/icons-material/ChildFriendly';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import WatchIcon from '@mui/icons-material/Watch';
import LanguageIcon from '@mui/icons-material/Language';
import PaletteIcon from '@mui/icons-material/Palette';
import WorkIcon from '@mui/icons-material/Work';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';


// Create a styled RetroTextField for our retro look
const RetroTextField = styled(TextField)(({ theme }) => ({
  marginBottom: '1rem',
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#1a237e',
    borderRadius: '5px',
    border: '2px solid #FFFFFF',
    '& fieldset': {
      border: 'none',
    },
    '& input': {
      fontFamily: "'Press Start 2P', cursive",
      color: '#FFFFFF',
      padding: '10px 12px',
    },
    '& input::placeholder': {
      color: '#ccc',
    },
    '&:hover': {
      boxShadow: '0 0 10px #9932CC',
    },
    '&.Mui-focused': {
      boxShadow: '0 0 10px #BA55D3',
    },
  },
}));

// Styled container with retro glowing style
const AnimatedGlowBox = styled(motion(Box), {
  shouldForwardProp: (prop) => prop !== 'godMode',
})(({ theme, godMode }) => ({
  width: '100%',
  maxWidth: '420px',
  minHeight: '380px',
  padding: '30px',
  margin: '20px auto',
  // Include the 30px padding in the 100%/420px width — without this the box
  // renders 60px wider than its container and gets clipped on the right edge
  // of mobile viewports (visible on Edge/Firefox for Android).
  boxSizing: 'border-box',
  // In God Mode the box goes transparent and GodCircleThree renders on a
  // separate stationary layer behind it (see render), so dragging the box
  // aside leaves the image in place for the viewer to see.
  backgroundColor: godMode ? 'transparent' : '#1a237e',
  borderRadius: '10px',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: '20px',
    // Square-ish on mobile / split-screen: at least as tall as the box is
    // wide. min(100vw, 420px) tracks the box's actual width (it's
    // width:100% with maxWidth:420px). Content sits at the top; empty
    // space below the search bar hints products will appear when the
    // user searches.
    minHeight: 'min(100vw, 420px)',
  },
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  color: '#FFFFFF',
  fontSize: '2rem',
  marginBottom: '20px',
  textShadow: `
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    1px 1px 0 #000
  `,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
}));

/**
 * Below is an expanded typicalProducts array with synonyms and variations.
 * In production, you’d keep adding until you have ~500 items covering
 * as many user queries as you expect.
 */
const typicalProducts = [
  // FOOD & GROCERIES
  { product: 'food', category: 'food' },
  { product: 'groceries', category: 'food' },
  { product: 'meal', category: 'food' },
  { product: 'cuisine', category: 'food' },
  { product: 'edibles', category: 'food' },
  { product: 'produce', category: 'food' },
  { product: 'snacks', category: 'food' },
  { product: 'chips', category: 'food' },
  { product: 'cookies', category: 'food' },
  { product: 'candy', category: 'food' },
  { product: 'chocolate', category: 'food' },
  { product: 'milk', category: 'food' },
  { product: 'milk products', category: 'food' },
  { product: 'bread', category: 'food' },
  { product: 'cheese', category: 'food' },
  { product: 'chicken', category: 'food' },
  { product: 'pork', category: 'food' },
  { product: 'beef', category: 'food' },
  { product: 'seafood', category: 'food' },
  { product: 'fish', category: 'food' },
  { product: 'rice', category: 'food' },
  { product: 'pasta', category: 'food' },
  { product: 'apples', category: 'food' },
  { product: 'oranges', category: 'food' },
  { product: 'carrots', category: 'food' },
  { product: 'vegetables', category: 'food' },
  { product: 'fruits', category: 'food' },
  { product: 'cereal', category: 'food' },
  { product: 'eggs', category: 'food' },
  { product: 'tea', category: 'food' },
  { product: 'coffee', category: 'food' },
  { product: 'soda', category: 'food' },
  { product: 'juice', category: 'food' },
  { product: 'water', category: 'food' },
  { product: 'organic food', category: 'food' },
  { product: 'frozen food', category: 'food' },
  { product: 'canned food', category: 'food' },
  { product: 'spices', category: 'food' },
  { product: 'condiments', category: 'food' },

  // BEVERAGES (including alcoholic - age-restricted)
  { product: 'beer', category: 'beverages' },
  { product: 'wine', category: 'beverages' },
  { product: 'liquor', category: 'beverages' },
  { product: 'spirits', category: 'beverages' },
  { product: 'cocktails', category: 'beverages' },

  // MOVIES / ENTERTAINMENT
  { product: 'movies', category: 'entertainment' },
  { product: 'films', category: 'entertainment' },
  { product: 'cinema', category: 'entertainment' },
  { product: 'dvd', category: 'entertainment' },
  { product: 'blu-ray', category: 'entertainment' },
  { product: 'comedy', category: 'entertainment' },
  { product: 'horror', category: 'entertainment' },
  { product: 'romance', category: 'entertainment' },
  { product: 'thriller', category: 'entertainment' },
  { product: 'drama', category: 'entertainment' },
  { product: 'music', category: 'entertainment' },

  // CLOTHING / FASHION
  { product: 'clothing', category: 'fashion' },
  { product: 'apparel', category: 'fashion' },
  { product: 'garments', category: 'fashion' },
  { product: 'footwear', category: 'fashion' },
  { product: 'shoes', category: 'fashion' },
  { product: 'boots', category: 'fashion' },
  { product: 'heels', category: 'fashion' },
  { product: 'dress', category: 'fashion' },
  { product: 'dresses', category: 'fashion' },
  { product: 'purse', category: 'fashion' },
  { product: 'handbag', category: 'fashion' },
  { product: 'bag', category: 'fashion' },
  { product: 'jewelry', category: 'fashion' },
  { product: 'necklaces', category: 'fashion' },
  { product: 'rings', category: 'fashion' },
  { product: 'bracelets', category: 'fashion' },
  { product: 'silver ring', category: 'fashion' },
  { product: 'diamond ring', category: 'fashion' },
  { product: 't-shirts', category: 'fashion' },
  { product: 'jeans', category: 'fashion' },
  { product: 'sneakers', category: 'fashion' },
  { product: 'watches', category: 'watch' }, // Note: watch category
  { product: 'sunglasses', category: 'fashion' },

  // AUTOMOTIVE
  { product: 'cars', category: 'automotive' },
  { product: 'vehicles', category: 'automotive' },
  { product: 'automobiles', category: 'automotive' },
  { product: 'auto', category: 'automotive' },
  { product: 'car parts', category: 'automotive' },
  { product: 'tires', category: 'automotive' },
  { product: 'engine oil', category: 'automotive' },
  { product: 'spark plugs', category: 'automotive' },
  { product: 'shock absorbers', category: 'automotive' },
  { product: 'battery', category: 'automotive' },
  { product: 'bicycles', category: 'automotive' },
  { product: 'motorcycles', category: 'automotive' },
  { product: 'helmets', category: 'automotive' },

  // ELECTRONICS
  { product: 'electronics', category: 'electronics' },
  { product: 'phones', category: 'electronics' },
  { product: 'smartphones', category: 'electronics' },
  { product: 'mobile phones', category: 'electronics' },
  { product: 'pc', category: 'electronics' },
  { product: 'laptop', category: 'electronics' },
  { product: 'laptops', category: 'electronics' },
  { product: 'desktops', category: 'electronics' },
  { product: 'tablets', category: 'electronics' },
  { product: 'computer', category: 'electronics' },
  { product: 'monitor', category: 'electronics' },
  { product: 'tv', category: 'electronics' },
  { product: 'television', category: 'electronics' },
  { product: 'headphones', category: 'electronics' },
  { product: 'speakers', category: 'electronics' },
  { product: 'earbuds', category: 'electronics' },
  { product: 'bluetooth headphones', category: 'electronics' },
  { product: 'camera', category: 'electronics' },
  { product: 'cameras', category: 'electronics' },
  { product: 'dslr', category: 'electronics' },
  { product: 'mirrorless camera', category: 'electronics' },
  { product: 'phone chargers', category: 'electronics' },
  { product: 'laptop chargers', category: 'electronics' },
  { product: 'smartwatch', category: 'electronics' },
  { product: 'smartwatches', category: 'electronics' },
  { product: 'router', category: 'electronics' },
  { product: 'routers', category: 'electronics' },
  { product: 'vr headset', category: 'electronics' },
  { product: 'virtual reality', category: 'electronics' },
  { product: 'graphics card', category: 'electronics' },
  { product: 'gpu', category: 'electronics' },

  // GAMING
  { product: 'gaming', category: 'gaming' },
  { product: 'games', category: 'gaming' },
  { product: 'video games', category: 'gaming' },
  { product: 'steam keys', category: 'gaming' },
  { product: 'ps5', category: 'gaming' },
  { product: 'nintendo switch', category: 'gaming' },
  { product: 'xbox', category: 'gaming' },
  { product: 'consoles', category: 'gaming' },
  { product: 'game discs', category: 'gaming' },
  { product: 'controllers', category: 'gaming' },
  { product: 'gaming pc', category: 'gaming' },
  { product: 'gaming laptop', category: 'gaming' },
  { product: 'joystick', category: 'gaming' },

  // SOFTWARE
  { product: 'software', category: 'software' },
  { product: 'apps', category: 'software' },
  { product: 'vpn', category: 'software' },
  { product: 'antivirus', category: 'software' },
  { product: 'office suite', category: 'software' },
  { product: 'operating system', category: 'software' },
  { product: 'vpn subscriptions', category: 'software' },

  // INTERNET SERVICES
  { product: 'internet services', category: 'internetServices' },
  { product: 'hosting', category: 'internetServices' },
  { product: 'domains', category: 'internetServices' },
  { product: 'domain names', category: 'internetServices' },
  { product: 'domain registration', category: 'internetServices' },

  // TELECOM
  { product: 'telecom', category: 'telecom' },
  { product: 'phone plan', category: 'telecom' },
  { product: 'mobile plan', category: 'telecom' },
  { product: 'internet plan', category: 'telecom' },
  { product: 'telecommunications', category: 'telecom' },
  { product: 'landline', category: 'telecom' },

  // FURNITURE
  { product: 'furniture', category: 'furniture' },
  { product: 'sofa', category: 'furniture' },
  { product: 'couch', category: 'furniture' },
  { product: 'bed', category: 'furniture' },
  { product: 'desk', category: 'furniture' },
  { product: 'wardrobe', category: 'furniture' },
  { product: 'dresser', category: 'furniture' },
  { product: 'mirror', category: 'furniture' },
  { product: 'nightstand', category: 'furniture' },
  { product: 'tables', category: 'furniture' },
  { product: 'chairs', category: 'furniture' },
  { product: 'lamps', category: 'furniture' },
  { product: 'lighting', category: 'furniture' },
  { product: 'beds', category: 'furniture' },
  { product: 'sofas', category: 'furniture' },

  // HOME
  { product: 'home goods', category: 'home' },
  { product: 'home improvement', category: 'home' },
  { product: 'appliances', category: 'home' },
  { product: 'kitchen', category: 'home' },
  { product: 'decor', category: 'home' },
  { product: 'garden', category: 'home' },
  { product: 'tools', category: 'home' },
  { product: 'hardware', category: 'home' },

  // BEAUTY
  { product: 'beauty', category: 'beauty' },
  { product: 'makeup', category: 'beauty' },
  { product: 'cosmetics', category: 'beauty' },
  { product: 'lipstick', category: 'beauty' },
  { product: 'skincare', category: 'beauty' },
  { product: 'lotion', category: 'beauty' },
  { product: 'perfume', category: 'beauty' },

  // HEALTH
  { product: 'health', category: 'health' },
  { product: 'medicine', category: 'health' },
  { product: 'medication', category: 'health' },
  { product: 'vitamins', category: 'health' },
  { product: 'supplements', category: 'health' },
  { product: 'pharmacy', category: 'health' },
  { product: 'wellness', category: 'health' },
  { product: 'first aid', category: 'health' },

  // SPORTS & OUTDOORS
  { product: 'sports', category: 'sports' },
  { product: 'fitness', category: 'sports' },
  { product: 'gym equipment', category: 'sports' },
  { product: 'workout', category: 'sports' },
  { product: 'athletic wear', category: 'sports' },
  { product: 'running shoes', category: 'sports' },
  { product: 'camping gear', category: 'sports' },
  { product: 'outdoors', category: 'sports' },

  // PETS
  { product: 'pets', category: 'pets' },
  { product: 'pet food', category: 'pets' },
  { product: 'dog food', category: 'pets' },
  { product: 'cat food', category: 'pets' },
  { product: 'pet supplies', category: 'pets' },
  { product: 'dog toys', category: 'pets' },
  { product: 'cat toys', category: 'pets' },
  { product: 'pet accessories', category: 'pets' },
  { product: 'veterinary', category: 'pets' },

  // BABY
  { product: 'baby', category: 'baby' },
  { product: 'babies', category: 'baby' },
  { product: 'diapers', category: 'baby' },
  { product: 'stroller', category: 'baby' },
  { product: 'strollers', category: 'baby' },
  { product: 'baby food', category: 'baby' },
  { product: 'infant', category: 'baby' },
  { product: 'crib', category: 'baby' },
  { product: 'baby toys', category: 'baby' },
  { product: 'maternity', category: 'baby' },

  // ART
  { product: 'art', category: 'art' },
  { product: 'paintings', category: 'art' },
  { product: 'sculptures', category: 'art' },
  { product: 'drawings', category: 'art' },
  { product: 'photography', category: 'art' },
  { product: 'print', category: 'art' },
  { product: 'canvas', category: 'art' },
  { product: 'antiques', category: 'art' },

  // SPECIALTY
  { product: 'crystals', category: 'specialty' },
  { product: 'outdoors', category: 'specialty' },
  { product: 'petlove', category: 'specialty' },
  // Already in your mapping: seedsman, vapePenStore, etc.

  // NFT
  { product: 'nft', category: 'nft' },
  { product: 'digital art', category: 'nft' },
  { product: 'digital collectibles', category: 'nft' },
  { product: 'rare digital items', category: 'nft' },

  // GIFTCARDS
  { product: 'gift cards', category: 'giftcards' },
  { product: 'vouchers', category: 'giftcards' },
  { product: 'steam gift cards', category: 'giftcards' },
  { product: 'amazon gift cards', category: 'giftcards' },
  { product: 'google play gift cards', category: 'giftcards' },
  { product: 'itunes gift cards', category: 'giftcards' },

  // EDUCATION
  { product: 'books', category: 'education' },
  { product: 'magazines', category: 'education' },
  { product: 'journals', category: 'education' },
  { product: 'school', category: 'education' },
  { product: 'university', category: 'education' },
  { product: 'college', category: 'education' },
  { product: 'course', category: 'education' },
  { product: 'class', category: 'education' },

  // TRAVEL
  { product: 'travel', category: 'travel' },
  { product: 'trip', category: 'travel' },
  { product: 'vacation', category: 'travel' },
  { product: 'flight', category: 'travel' },
  { product: 'hotel', category: 'travel' },
  { product: 'train', category: 'travel' },
  { product: 'bus', category: 'travel' },
  { product: 'car rental', category: 'travel' },
  { product: 'rental car', category: 'travel' },
  { product: 'accommodation', category: 'travel' },
  { product: 'lodging', category: 'travel' },
  { product: 'resort', category: 'travel' },
  { product: 'cruise', category: 'travel' },
  { product: 'backpacking', category: 'travel' },
  { product: 'sightseeing', category: 'travel' },

  // HOBBIES & CRAFTS
  { product: 'crafts', category: 'crafts' },
  { product: 'hobby', category: 'crafts' },
  { product: 'sewing', category: 'crafts' },
  { product: 'knitting', category: 'crafts' },
  { product: 'scrapbooking', category: 'crafts' },
  { product: 'painting supplies', category: 'crafts' },
  { product: 'art supplies', category: 'crafts' },
  { product: 'yarn', category: 'crafts' },
  { product: 'fabric', category: 'crafts' },
  { product: 'beads', category: 'crafts' },

  // PROFESSIONAL
  { product: 'professional', category: 'professional' },
  { product: 'services', category: 'professional' },
  { product: 'consulting', category: 'professional' },
  { product: 'legal', category: 'professional' },
  { product: 'accounting', category: 'professional' },

  // PRECIOUS METALS
  { product: 'gold', category: 'metals' },
  { product: 'silver', category: 'metals' },
  { product: 'bullion', category: 'metals' },
  { product: 'precious metals', category: 'metals' },
  { product: 'gold coins', category: 'metals' },
  { product: 'silver coins', category: 'metals' },
  { product: 'gold bars', category: 'metals' },
  { product: 'platinum', category: 'metals' },

  // CHARITY / DONATIONS
  { product: 'charity', category: 'charity' },
  { product: 'donate', category: 'charity' },
  { product: 'donation', category: 'charity' },
  { product: 'donations', category: 'charity' },
  { product: 'nonprofit', category: 'charity' },
  { product: 'giving', category: 'charity' },

  // MORE FOOD
  { product: 'pizza', category: 'food' },
  { product: 'burgers', category: 'food' },
  { product: 'fast food', category: 'food' },
  { product: 'takeout', category: 'food' },
  { product: 'food delivery', category: 'food' },

  // MORE ENTERTAINMENT
  { product: 'movie tickets', category: 'entertainment' },
  { product: 'concerts', category: 'entertainment' },
  { product: 'concert tickets', category: 'entertainment' },
  { product: 'theater', category: 'entertainment' },
  { product: 'tickets', category: 'entertainment' },
  { product: 'events', category: 'entertainment' },

  // MORE ELECTRONICS
  { product: 'drone', category: 'electronics' },
  { product: 'drones', category: 'electronics' },
  { product: '3d printer', category: 'electronics' },
  { product: 'printer', category: 'electronics' },
  { product: 'keyboard', category: 'electronics' },
  { product: 'mouse', category: 'electronics' },
  { product: 'ssd', category: 'electronics' },
  { product: 'hard drive', category: 'electronics' },

  // MORE TRAVEL
  { product: 'airline tickets', category: 'travel' },
  { product: 'plane tickets', category: 'travel' },
  { product: 'airfare', category: 'travel' },
  { product: 'honeymoon', category: 'travel' },

  // MORE SOFTWARE / PRIVACY
  { product: 'email', category: 'software' },
  { product: 'secure email', category: 'software' },
  { product: 'privacy', category: 'software' },
  { product: 'password manager', category: 'software' },

  // MORE INTERNET SERVICES
  { product: 'vps', category: 'internetServices' },
  { product: 'server', category: 'internetServices' },
  { product: 'cloud hosting', category: 'internetServices' },
  { product: 'website', category: 'internetServices' },

  // MORE FASHION / WATCHES
  { product: 'luxury', category: 'fashion' },
  { product: 'designer', category: 'fashion' },
  { product: 'streetwear', category: 'fashion' },
  { product: 'rolex', category: 'watch' },
  { product: 'luxury watches', category: 'watch' },

  // MORE AUTOMOTIVE
  { product: 'used cars', category: 'automotive' },
  { product: 'luxury car', category: 'automotive' },
  { product: 'supercar', category: 'automotive' },

  // MORE GIFT CARDS
  { product: 'netflix', category: 'giftcards' },
  { product: 'spotify', category: 'giftcards' },
  { product: 'playstation gift cards', category: 'giftcards' },
  { product: 'xbox gift cards', category: 'giftcards' },
  { product: 'uber gift cards', category: 'giftcards' },
  { product: 'doordash gift cards', category: 'giftcards' },

  // PERSONAL CARE (iHerb/Vitacost carry all of these)
  { product: 'soap', category: 'health' },
  { product: 'deodorant', category: 'health' },
  { product: 'shampoo', category: 'health' },
  { product: 'conditioner', category: 'health' },
  { product: 'body wash', category: 'health' },
  { product: 'toothpaste', category: 'health' },
  { product: 'toothbrush', category: 'health' },
  { product: 'mouthwash', category: 'health' },
  { product: 'floss', category: 'health' },
  { product: 'razors', category: 'health' },
  { product: 'shaving cream', category: 'health' },
  { product: 'hand sanitizer', category: 'health' },
  { product: 'sunscreen', category: 'health' },
  { product: 'feminine care', category: 'health' },
  { product: 'protein powder', category: 'health' },
  { product: 'protein bars', category: 'health' },
  { product: 'probiotics', category: 'health' },
  { product: 'allergy medicine', category: 'health' },
  { product: 'pain relief', category: 'health' },
  { product: 'band aids', category: 'health' },

  // HOUSEHOLD ITEMS & CLEANING (gift-card route via retail)
  { product: 'household items', category: 'retail' },
  { product: 'household', category: 'retail' },
  { product: 'cleaning supplies', category: 'retail' },
  { product: 'detergent', category: 'retail' },
  { product: 'laundry detergent', category: 'retail' },
  { product: 'dish soap', category: 'retail' },
  { product: 'paper towels', category: 'retail' },
  { product: 'toilet paper', category: 'retail' },
  { product: 'trash bags', category: 'retail' },
  { product: 'sponges', category: 'retail' },
  { product: 'air freshener', category: 'retail' },
  { product: 'batteries', category: 'retail' },
  { product: 'storage bins', category: 'retail' },

  // KITCHEN & HOME GOODS
  { product: 'light bulbs', category: 'home' },
  { product: 'paint', category: 'home' },
  { product: 'lawn mower', category: 'home' },
  { product: 'grill', category: 'home' },
  { product: 'bbq', category: 'home' },
  { product: 'vacuum', category: 'home' },
  { product: 'blender', category: 'home' },
  { product: 'microwave', category: 'home' },
  { product: 'refrigerator', category: 'home' },
  { product: 'washer', category: 'home' },
  { product: 'dryer', category: 'home' },
  { product: 'cookware', category: 'home' },
  { product: 'pots and pans', category: 'home' },
  { product: 'dishes', category: 'home' },
  { product: 'silverware', category: 'home' },
  { product: 'bedding', category: 'home' },
  { product: 'sheets', category: 'home' },
  { product: 'pillows', category: 'home' },
  { product: 'towels', category: 'home' },
  { product: 'curtains', category: 'home' },
  { product: 'rugs', category: 'home' },
  { product: 'plants', category: 'home' },
  { product: 'mattress', category: 'furniture' },
  { product: 'bookshelf', category: 'furniture' },
  { product: 'recliner', category: 'furniture' },
  { product: 'patio furniture', category: 'furniture' },

  // CLOTHING BASICS
  { product: 'socks', category: 'fashion' },
  { product: 'underwear', category: 'fashion' },
  { product: 'hats', category: 'fashion' },
  { product: 'belts', category: 'fashion' },
  { product: 'gloves', category: 'fashion' },
  { product: 'scarves', category: 'fashion' },
  { product: 'coats', category: 'fashion' },
  { product: 'jackets', category: 'fashion' },
  { product: 'hoodies', category: 'fashion' },
  { product: 'sweaters', category: 'fashion' },
  { product: 'shorts', category: 'fashion' },
  { product: 'skirts', category: 'fashion' },
  { product: 'suits', category: 'fashion' },
  { product: 'ties', category: 'fashion' },
  { product: 'swimwear', category: 'fashion' },
  { product: 'pajamas', category: 'fashion' },
  { product: 'backpacks', category: 'fashion' },
  { product: 'wallets', category: 'fashion' },
  { product: 'sandals', category: 'fashion' },
  { product: 'slippers', category: 'fashion' },
  { product: 'luggage', category: 'travel' },
  { product: 'suitcase', category: 'travel' },

  // MORE ELECTRONICS & GADGETS
  { product: 'earphones', category: 'electronics' },
  { product: 'airpods', category: 'electronics' },
  { product: 'charger', category: 'electronics' },
  { product: 'power bank', category: 'electronics' },
  { product: 'usb cable', category: 'electronics' },
  { product: 'hdmi cable', category: 'electronics' },
  { product: 'webcam', category: 'electronics' },
  { product: 'microphone', category: 'electronics' },
  { product: 'soundbar', category: 'electronics' },
  { product: 'projector', category: 'electronics' },
  { product: 'smart home', category: 'electronics' },
  { product: 'alexa', category: 'electronics' },
  { product: 'ring doorbell', category: 'electronics' },
  { product: 'security camera', category: 'electronics' },
  { product: 'iphone', category: 'electronics' },
  { product: 'samsung phone', category: 'electronics' },
  { product: 'macbook', category: 'electronics' },
  { product: 'chromebook', category: 'electronics' },
  { product: 'ipad', category: 'electronics' },
  { product: 'kindle', category: 'electronics' },

  // MORE FOOD & DRINK
  { product: 'steak', category: 'food' },
  { product: 'sushi', category: 'food' },
  { product: 'tacos', category: 'food' },
  { product: 'burritos', category: 'food' },
  { product: 'sandwiches', category: 'food' },
  { product: 'dessert', category: 'food' },
  { product: 'ice cream', category: 'food' },
  { product: 'cake', category: 'food' },
  { product: 'bakery', category: 'food' },
  { product: 'energy drinks', category: 'food' },
  { product: 'hot sauce', category: 'food' },
  { product: 'coffee beans', category: 'food' },
  { product: 'whiskey', category: 'beverages' },
  { product: 'vodka', category: 'beverages' },
  { product: 'tequila', category: 'beverages' },
  { product: 'rum', category: 'beverages' },
  { product: 'champagne', category: 'beverages' },
  { product: 'bourbon', category: 'beverages' },

  // SPORTS & OUTDOORS GEAR
  { product: 'dumbbells', category: 'sports' },
  { product: 'weights', category: 'sports' },
  { product: 'treadmill', category: 'sports' },
  { product: 'yoga mat', category: 'sports' },
  { product: 'golf', category: 'sports' },
  { product: 'golf clubs', category: 'sports' },
  { product: 'tennis', category: 'sports' },
  { product: 'basketball', category: 'sports' },
  { product: 'football', category: 'sports' },
  { product: 'baseball', category: 'sports' },
  { product: 'soccer ball', category: 'sports' },
  { product: 'fishing', category: 'sports' },
  { product: 'kayak', category: 'sports' },
  { product: 'skateboard', category: 'sports' },
  { product: 'snowboard', category: 'sports' },
  { product: 'skis', category: 'sports' },
  { product: 'hiking boots', category: 'sports' },
  { product: 'tent', category: 'sports' },
  { product: 'sleeping bag', category: 'sports' },

  // MORE PET SUPPLIES
  { product: 'leash', category: 'pets' },
  { product: 'dog collar', category: 'pets' },
  { product: 'dog crate', category: 'pets' },
  { product: 'dog treats', category: 'pets' },
  { product: 'cat litter', category: 'pets' },
  { product: 'litter box', category: 'pets' },
  { product: 'aquarium', category: 'pets' },
  { product: 'fish tank', category: 'pets' },
  { product: 'bird cage', category: 'pets' },

  // MORE BABY
  { product: 'baby clothes', category: 'baby' },
  { product: 'car seat', category: 'baby' },
  { product: 'high chair', category: 'baby' },
  { product: 'baby monitor', category: 'baby' },
  { product: 'formula', category: 'baby' },
  { product: 'baby wipes', category: 'baby' },

  // MORE BEAUTY
  { product: 'nail polish', category: 'beauty' },
  { product: 'hair dye', category: 'beauty' },
  { product: 'curling iron', category: 'beauty' },
  { product: 'hair dryer', category: 'beauty' },
  { product: 'moisturizer', category: 'beauty' },
  { product: 'serum', category: 'beauty' },
  { product: 'mascara', category: 'beauty' },
  { product: 'foundation', category: 'beauty' },
  { product: 'eyeliner', category: 'beauty' },

  // MORE GAMING
  { product: 'fortnite', category: 'gaming' },
  { product: 'roblox', category: 'gaming' },
  { product: 'minecraft', category: 'gaming' },
  { product: 'gaming chair', category: 'gaming' },
  { product: 'gaming headset', category: 'gaming' },

  // MORE METALS & COINS
  { product: 'silver bars', category: 'metals' },
  { product: 'gold eagle', category: 'metals' },
  { product: 'krugerrand', category: 'metals' },
  { product: 'rare coins', category: 'metals' },
  { product: 'coin collection', category: 'metals' },

  // MORE SOFTWARE & SERVICES
  { product: 'photoshop', category: 'software' },
  { product: 'microsoft office', category: 'software' },
  { product: 'windows key', category: 'software' },
  { product: 'cloud storage', category: 'software' },
  { product: 'ssl certificate', category: 'internetServices' },
  { product: 'web hosting', category: 'internetServices' },

  // MORE AUTOMOTIVE
  { product: 'motor oil', category: 'automotive' },
  { product: 'brake pads', category: 'automotive' },
  { product: 'car battery', category: 'automotive' },
  { product: 'wipers', category: 'automotive' },
  { product: 'floor mats', category: 'automotive' },
  { product: 'rims', category: 'automotive' },
  { product: 'wheels', category: 'automotive' },
  { product: 'truck parts', category: 'automotive' },
  { product: 'motorcycle helmet', category: 'automotive' },

  // MORE TRAVEL & EVENTS
  { product: 'theme park', category: 'travel' },
  { product: 'disney world', category: 'travel' },
  { product: 'airbnb', category: 'travel' },
  { product: 'road trip', category: 'travel' },
  { product: 'nba tickets', category: 'entertainment' },
  { product: 'sports tickets', category: 'entertainment' },
  { product: 'streaming', category: 'giftcards' },

  // BRAND-NAME SEARCHES (crypto route = gift cards)
  { product: 'walmart', category: 'giftcards' },
  { product: 'target', category: 'giftcards' },
  { product: 'amazon', category: 'giftcards' },
  { product: 'ebay', category: 'giftcards' },
  { product: 'starbucks', category: 'giftcards' },
  { product: 'dunkin', category: 'giftcards' },
  { product: 'apple gift card', category: 'giftcards' },
  { product: 'visa gift card', category: 'giftcards' },
  { product: 'grubhub', category: 'giftcards' },
  { product: 'instacart', category: 'giftcards' },
  { product: 'home depot gift card', category: 'giftcards' },
  { product: 'airbnb gift card', category: 'giftcards' },
  { product: 'southwest', category: 'giftcards' },
  { product: 'delta', category: 'giftcards' },

  // CHARITY EXTRAS
  { product: 'church donation', category: 'charity' },
  { product: 'disaster relief', category: 'charity' },
  { product: 'food bank', category: 'charity' },

  // FALLBACK EXAMPLES
  { product: 'misc', category: 'retail' },
  { product: 'stuff', category: 'retail' },
];

/**
 * Our retailer suggestions mapping (from your CSV data and previous mappings).
 * Each key corresponds to a category, which we'll look up from typicalProducts.
 */
const suggestionsMapping = {
  automotive: [
    // US - Crypto car sales
    { icon: <DirectionsCarIcon fontSize="large" />, label: 'AutocoinCar (US)', url: 'https://autocoincar.com' },
    // US - Premium auto parts
    { icon: <DirectionsCarIcon fontSize="large" />, label: 'RockAuto (US)', url: 'https://rockauto.com' },
    // US - Luxury dealer, BTC pioneer
    { icon: <DirectionsCarIcon fontSize="large" />, label: 'Post Oak Motors (US)', url: 'https://postoakmotors.com' },
  ],
  electronics: [
    // US - Major crypto accepting retailers
    { icon: <LaptopMacIcon fontSize="large" />, label: 'Newegg (US)', url: 'https://newegg.com' },
    { icon: <LaptopMacIcon fontSize="large" />, label: 'Microsoft (US)', url: 'https://microsoft.com' },
    { icon: <LaptopMacIcon fontSize="large" />, label: 'B&H Photo (US)', url: 'https://bhphotovideo.com' },
  ],
  retail: [
    // US - Major crypto retailers
    { icon: <StorefrontIcon fontSize="large" />, label: 'Overstock (US)', url: 'https://overstock.com' },
    { icon: <StorefrontIcon fontSize="large" />, label: 'Shopinbit', url: 'https://shopinbit.com' },
    { icon: <StorefrontIcon fontSize="large" />, label: 'eGifter (US)', url: 'https://egifter.com' },
    // Global - Crypto pioneer, big with US users
    { icon: <StorefrontIcon fontSize="large" />, label: 'Bitrefill (Global)', url: 'https://bitrefill.com' },
    // Global - Crypto-only marketplace (electronics to cars)
    { icon: <StorefrontIcon fontSize="large" />, label: 'Crypto Emporium (Global)', url: 'https://cryptoemporium.eu' },
  ],
  travel: [
    // Global - Crypto travel leader, popular with US travelers
    { icon: <FlightIcon fontSize="large" />, label: 'Travala (Global)', url: 'https://travala.com' },
    // US - Pioneer crypto flights
    { icon: <FlightIcon fontSize="large" />, label: 'CheapAir (US)', url: 'https://cheapair.com' },
    // Global - Books 600+ airlines for US flyers
    { icon: <FlightIcon fontSize="large" />, label: 'Alternative Airlines (Global)', url: 'https://alternativeairlines.com' },
  ],
  fashion: [
    // Global - Luxury e-tailers popular with US shoppers
    { icon: <LocalMallIcon fontSize="large" />, label: 'LuisaViaRoma (Global)', url: 'https://luisaviaroma.com' },
    { icon: <LocalMallIcon fontSize="large" />, label: 'Farfetch (Global)', url: 'https://farfetch.com' },
    // US - Watches & accessories
    { icon: <LocalMallIcon fontSize="large" />, label: 'Jomashop (US)', url: 'https://jomashop.com' },
    // US - Small business fashion
    { icon: <LocalMallIcon fontSize="large" />, label: 'Sezzle (US)', url: 'https://sezzle.com' },
    // US - Streetwear chain, BitPay checkout
    { icon: <LocalMallIcon fontSize="large" />, label: 'Pacsun (US)', url: 'https://pacsun.com' },
    // Global - Designer accepting crypto
    { icon: <LocalMallIcon fontSize="large" />, label: 'Philipp Plein (Global)', url: 'https://plein.com' },
  ],
  watch: [
    { icon: <LocalMallIcon fontSize="large" />, label: 'Jomashop (US)', url: 'https://jomashop.com' },
    // Global - Crypto-native luxury watches, ships to US
    { icon: <LocalMallIcon fontSize="large" />, label: 'BitDials (Global)', url: 'https://bitdials.eu' },
    // US - Jewelry chain, BTC via BitPay
    { icon: <DiamondIcon fontSize="large" />, label: 'REEDS Jewelers (US)', url: 'https://reeds.com' },
    // Global - Crypto-only luxury marketplace
    { icon: <DiamondIcon fontSize="large" />, label: 'Crypto Emporium (Global)', url: 'https://cryptoemporium.eu' },
  ],
  internetServices: [
    { icon: <ComputerIcon fontSize="large" />, label: 'Namecheap (US)', url: 'https://namecheap.com' },
    { icon: <ComputerIcon fontSize="large" />, label: 'WordPress (US)', url: 'https://wordpress.com' },
    { icon: <ComputerIcon fontSize="large" />, label: 'Hostinger (Global)', url: 'https://hostinger.com' },
    // US - Cloud servers for BTC
    { icon: <ComputerIcon fontSize="large" />, label: 'Vultr (US)', url: 'https://vultr.com' },
    { icon: <ComputerIcon fontSize="large" />, label: 'NameSilo (US)', url: 'https://namesilo.com' },
  ],
  software: [
    // VPNs - all heavily used by US subscribers
    { icon: <LaptopMacIcon fontSize="large" />, label: 'ExpressVPN (Global)', url: 'https://expressvpn.com' },
    { icon: <LaptopMacIcon fontSize="large" />, label: 'NordVPN (Global)', url: 'https://nordvpn.com' },
    { icon: <LaptopMacIcon fontSize="large" />, label: 'Private Internet Access (US)', url: 'https://privateinternetaccess.com' },
    { icon: <LaptopMacIcon fontSize="large" />, label: 'CyberGhost (Global)', url: 'https://cyberghostvpn.com' },
    { icon: <LaptopMacIcon fontSize="large" />, label: 'Surfshark (Global)', url: 'https://surfshark.com' },
    // Private email/VPN, popular with US users
    { icon: <LaptopMacIcon fontSize="large" />, label: 'Proton (Global)', url: 'https://proton.me' },
    { icon: <LaptopMacIcon fontSize="large" />, label: 'Mullvad VPN (Global)', url: 'https://mullvad.net' },
  ],
  gaming: [
    // Global - Import games, popular with US gamers
    { icon: <SportsEsportsIcon fontSize="large" />, label: 'PlayAsia (Global)', url: 'https://playasia.com' },
    // US - Game time crypto
    { icon: <SportsEsportsIcon fontSize="large" />, label: 'GameTimeZone (US)', url: 'https://gametimezone.com' },
    // US - Crypto gaming marketplace
    { icon: <SportsEsportsIcon fontSize="large" />, label: 'LandsOfCrypto (US)', url: 'https://landsofcrypto.com' },
    // Global - Steam & game gift cards
    { icon: <SportsEsportsIcon fontSize="large" />, label: 'Bitrefill Gaming', url: 'https://bitrefill.com/buy/steam' },
  ],
  entertainment: [
    // US - Movie chain taking BTC/ETH/DOGE online
    { icon: <TvIcon fontSize="large" />, label: 'AMC Theatres (US)', url: 'https://amctheatres.com' },
    // US - NBA team, tickets & merch via BitPay
    { icon: <TvIcon fontSize="large" />, label: 'Dallas Mavericks (US)', url: 'https://mavs.com' },
    // US - Event & entertainment gift cards for crypto
    { icon: <TvIcon fontSize="large" />, label: 'eGifter Entertainment (US)', url: 'https://egifter.com' },
  ],
  food: [
    // US - Restaurant ordering crypto
    { icon: <RestaurantIcon fontSize="large" />, label: 'Menufy (US)', url: 'https://menufy.com' },
    { icon: <RestaurantIcon fontSize="large" />, label: 'Uber Eats (US)', url: 'https://ubereats.com' },
    { icon: <RestaurantIcon fontSize="large" />, label: 'Whole Foods (US)', url: 'https://wholefoodsmarket.com' },
    // US - Pay-with-crypto fast food
    { icon: <RestaurantIcon fontSize="large" />, label: 'Chipotle (US)', url: 'https://chipotle.com' },
    // US - Specialty food crypto
    { icon: <RestaurantIcon fontSize="large" />, label: 'PexPeppers (US)', url: 'https://pexpeppers.com' },
  ],
  telecom: [
    // US - BTC via BitPay
    { icon: <PhoneIphoneIcon fontSize="large" />, label: 'AT&T (US)', url: 'https://att.com' },
    // US - Satellite TV, BTC since 2014
    { icon: <TvIcon fontSize="large" />, label: 'DISH TV (US)', url: 'https://dish.com' },
    // Prepaid refills for US carriers with crypto
    { icon: <PhoneIphoneIcon fontSize="large" />, label: 'Bitrefill Refills (US)', url: 'https://bitrefill.com/refill' },
  ],
  education: [
    // US - First US college to accept BTC
    { icon: <MenuBookIcon fontSize="large" />, label: "The King's College (US)", url: 'https://tkc.edu' },
    // US - Accepts crypto for tuition
    { icon: <MenuBookIcon fontSize="large" />, label: 'Univ. of the Cumberlands (US)', url: 'https://ucumberlands.edu' },
    // Course & learning gift cards for crypto
    { icon: <MenuBookIcon fontSize="large" />, label: 'Coinsbee Learning Cards', url: 'https://coinsbee.com' },
  ],
  professional: [
    // US - Major law firms accepting BTC
    { icon: <WorkIcon fontSize="large" />, label: 'Perkins Coie (US)', url: 'https://perkinscoie.com' },
    { icon: <WorkIcon fontSize="large" />, label: 'Steptoe (US)', url: 'https://steptoe.com' },
    // US - Crypto tax prep, pay in crypto
    { icon: <WorkIcon fontSize="large" />, label: 'Bitcoin.Tax (US)', url: 'https://bitcoin.tax' },
  ],
  art: [
    // US - Art marketplace
    { icon: <DiamondIcon fontSize="large" />, label: 'Artsy (US)', url: 'https://artsy.net' },
    // Global auction houses with crypto sales, big US presence
    { icon: <DiamondIcon fontSize="large" />, label: "Sotheby's (US)", url: 'https://sothebys.com' },
    { icon: <DiamondIcon fontSize="large" />, label: "Christie's (US)", url: 'https://christies.com' },
  ],
  furniture: [
    // US - Crypto retail pioneer, furniture focus
    { icon: <WeekendIcon fontSize="large" />, label: 'Overstock (US)', url: 'https://overstock.com' },
    { icon: <WeekendIcon fontSize="large" />, label: 'Wayfair (US)', url: 'https://wayfair.com' },
    // US - Home gift cards for crypto
    { icon: <WeekendIcon fontSize="large" />, label: 'eGifter Home Cards (US)', url: 'https://egifter.com' },
  ],
  home: [
    { icon: <HomeIcon fontSize="large" />, label: 'Home Depot', url: 'https://homedepot.com' },
    { icon: <HomeIcon fontSize="large" />, label: "Lowe's", url: 'https://lowes.com' },
    { icon: <HomeIcon fontSize="large" />, label: 'Ikea', url: 'https://ikea.com' },
    { icon: <HomeIcon fontSize="large" />, label: 'Wayfair', url: 'https://wayfair.com' },
  ],
  beauty: [
    { icon: <FaceRetouchingNaturalIcon fontSize="large" />, label: 'Sephora (US)', url: 'https://sephora.com' },
    { icon: <FaceRetouchingNaturalIcon fontSize="large" />, label: 'Ulta (US)', url: 'https://ulta.com' },
    // Beauty brand gift cards for crypto
    { icon: <FaceRetouchingNaturalIcon fontSize="large" />, label: 'Coinsbee Beauty Cards', url: 'https://coinsbee.com' },
  ],
  health: [
    // US - Health & supplements crypto
    { icon: <HealthAndSafetyIcon fontSize="large" />, label: 'iHerb (US)', url: 'https://iherb.com' },
    { icon: <HealthAndSafetyIcon fontSize="large" />, label: 'Vitacost (US)', url: 'https://vitacost.com' },
    { icon: <HealthAndSafetyIcon fontSize="large" />, label: 'Swanson Vitamins (US)', url: 'https://swansonvitamins.com' },
  ],
  sports: [
    // US - Outdoor gear crypto
    { icon: <SportsSoccerIcon fontSize="large" />, label: 'REI (US)', url: 'https://rei.com' },
    { icon: <SportsSoccerIcon fontSize="large" />, label: 'Dick\'s Sporting (US)', url: 'https://dickssportinggoods.com' },
    // US - Fitness crypto specialty
    { icon: <SportsSoccerIcon fontSize="large" />, label: 'Rogue Fitness (US)', url: 'https://roguefitness.com' },
  ],
  pets: [
    { icon: <PetsIcon fontSize="large" />, label: 'PetSmart (US)', url: 'https://petsmart.com' },
    { icon: <PetsIcon fontSize="large" />, label: 'Chewy (US)', url: 'https://chewy.com' },
    { icon: <PetsIcon fontSize="large" />, label: 'Petco (US)', url: 'https://petco.com' },
  ],
  baby: [
    { icon: <ChildFriendlyIcon fontSize="large" />, label: 'BuyBuyBaby', url: 'https://buybuybaby.com' },
    { icon: <ChildFriendlyIcon fontSize="large" />, label: 'Babylist', url: 'https://babylist.com' },
    { icon: <ChildFriendlyIcon fontSize="large" />, label: "Carter's", url: 'https://carters.com' },
    { icon: <ChildFriendlyIcon fontSize="large" />, label: 'Honest Company', url: 'https://honest.com' },
  ],
  specialty: [
    { icon: <StorefrontIcon fontSize="large" />, label: 'Etsy (US)', url: 'https://etsy.com' },
    { icon: <StorefrontIcon fontSize="large" />, label: 'Hobby Lobby (US)', url: 'https://hobbylobby.com' },
    { icon: <StorefrontIcon fontSize="large" />, label: 'Michaels (US)', url: 'https://michaels.com' },
    { icon: <StorefrontIcon fontSize="large" />, label: 'Joann Fabrics (US)', url: 'https://joann.com' },
  ],
  nft: [
    { icon: <DiamondIcon fontSize="large" />, label: 'OpenSea', url: 'https://opensea.io' },
    { icon: <DiamondIcon fontSize="large" />, label: 'Blur', url: 'https://blur.io' },
    { icon: <DiamondIcon fontSize="large" />, label: 'Rarible', url: 'https://rarible.com' },
    { icon: <DiamondIcon fontSize="large" />, label: 'NiftyGateway', url: 'https://niftygateway.com' },
    { icon: <DiamondIcon fontSize="large" />, label: 'Foundation', url: 'https://foundation.app' },
  ],
  giftcards: [
    // US - Pioneer crypto gift cards
    { icon: <CreditCardIcon fontSize="large" />, label: 'eGifter (US)', url: 'https://egifter.com' },
    // Global - Crypto gift card leader
    { icon: <CreditCardIcon fontSize="large" />, label: 'Bitrefill (Global)', url: 'https://bitrefill.com' },
    // Canada - North American alternative
    { icon: <CreditCardIcon fontSize="large" />, label: 'CoinCards (CA)', url: 'https://coincards.com' },
    // Germany - 5000+ brands for crypto
    { icon: <CreditCardIcon fontSize="large" />, label: 'Coinsbee (Global)', url: 'https://coinsbee.com' },
    // Netherlands - Prepaid & gift cards
    { icon: <CreditCardIcon fontSize="large" />, label: 'Dundle (Global)', url: 'https://dundle.com' },
  ],
  metals: [
    // US - All major bullion dealers take BTC
    { icon: <MonetizationOnIcon fontSize="large" />, label: 'JM Bullion (US)', url: 'https://jmbullion.com' },
    { icon: <MonetizationOnIcon fontSize="large" />, label: 'APMEX (US)', url: 'https://apmex.com' },
    { icon: <MonetizationOnIcon fontSize="large" />, label: 'SD Bullion (US)', url: 'https://sdbullion.com' },
    { icon: <MonetizationOnIcon fontSize="large" />, label: 'Money Metals (US)', url: 'https://moneymetals.com' },
    { icon: <MonetizationOnIcon fontSize="large" />, label: 'Bullion Exchanges (US)', url: 'https://bullionexchanges.com' },
    { icon: <MonetizationOnIcon fontSize="large" />, label: 'Kitco (CA)', url: 'https://kitco.com' },
  ],
  charity: [
    // Long-standing crypto donation programs
    { icon: <VolunteerActivismIcon fontSize="large" />, label: 'Wikipedia (Wikimedia)', url: 'https://donate.wikimedia.org' },
    { icon: <VolunteerActivismIcon fontSize="large" />, label: 'American Red Cross', url: 'https://redcross.org' },
    { icon: <VolunteerActivismIcon fontSize="large" />, label: 'Save the Children', url: 'https://savethechildren.org' },
    { icon: <VolunteerActivismIcon fontSize="large" />, label: 'Internet Archive', url: 'https://archive.org/donate' },
    { icon: <VolunteerActivismIcon fontSize="large" />, label: 'UNICEF CryptoFund', url: 'https://unicef.org/innovation/stories/unicef-cryptofund' },
    { icon: <VolunteerActivismIcon fontSize="large" />, label: 'The Giving Block', url: 'https://thegivingblock.com' },
  ],
  crafts: [
    { icon: <DiamondIcon fontSize="large" />, label: 'Etsy', url: 'https://etsy.com' },
    { icon: <DiamondIcon fontSize="large" />, label: 'Michaels', url: 'https://michaels.com' },
    { icon: <DiamondIcon fontSize="large" />, label: 'Hobby Lobby', url: 'https://hobbylobby.com' },
    { icon: <DiamondIcon fontSize="large" />, label: 'Joann Fabrics', url: 'https://joann.com' },
    { icon: <DiamondIcon fontSize="large" />, label: 'Amazon Crafts', url: 'https://amazon.com/crafts' },
    { icon: <DiamondIcon fontSize="large" />, label: 'Blick Art', url: 'https://dickblick.com' },
  ],
  beverages: [
    { icon: <LocalBarIcon fontSize="large" />, label: 'Total Wine (US)', url: 'https://totalwine.com' },
    { icon: <LocalBarIcon fontSize="large" />, label: 'BevMo! (US)', url: 'https://bevmo.com' },
    { icon: <LocalBarIcon fontSize="large" />, label: 'Wine.com (US)', url: 'https://wine.com' },
    { icon: <LocalBarIcon fontSize="large" />, label: 'ReserveBar (US)', url: 'https://reservebar.com' },
  ],
};

/**
 * Display metadata for every category: icon + short label, in the order the
 * browse grid shows them. Every key here must exist in suggestionsMapping.
 */
const CATEGORY_META = [
  { key: 'electronics', label: 'Electronics', icon: <LaptopMacIcon /> },
  { key: 'fashion', label: 'Fashion', icon: <LocalMallIcon /> },
  { key: 'food', label: 'Food', icon: <RestaurantIcon /> },
  { key: 'travel', label: 'Travel', icon: <FlightIcon /> },
  { key: 'gaming', label: 'Gaming', icon: <SportsEsportsIcon /> },
  { key: 'giftcards', label: 'Gift Cards', icon: <CardGiftcardIcon /> },
  { key: 'metals', label: 'Gold/Silver', icon: <MonetizationOnIcon /> },
  { key: 'home', label: 'Home', icon: <HomeIcon /> },
  { key: 'automotive', label: 'Auto', icon: <DirectionsCarIcon /> },
  { key: 'retail', label: 'Retail', icon: <StorefrontIcon /> },
  { key: 'health', label: 'Health', icon: <HealthAndSafetyIcon /> },
  { key: 'beauty', label: 'Beauty', icon: <FaceRetouchingNaturalIcon /> },
  { key: 'sports', label: 'Sports', icon: <SportsSoccerIcon /> },
  { key: 'pets', label: 'Pets', icon: <PetsIcon /> },
  { key: 'baby', label: 'Baby', icon: <ChildFriendlyIcon /> },
  { key: 'furniture', label: 'Furniture', icon: <WeekendIcon /> },
  { key: 'entertainment', label: 'Shows', icon: <TvIcon /> },
  { key: 'beverages', label: 'Drinks', icon: <LocalBarIcon /> },
  { key: 'watch', label: 'Watches', icon: <WatchIcon /> },
  { key: 'software', label: 'Software', icon: <ComputerIcon /> },
  { key: 'internetServices', label: 'Web/Domains', icon: <LanguageIcon /> },
  { key: 'telecom', label: 'Telecom', icon: <PhoneIphoneIcon /> },
  { key: 'education', label: 'Education', icon: <MenuBookIcon /> },
  { key: 'art', label: 'Art', icon: <DiamondIcon /> },
  { key: 'crafts', label: 'Crafts', icon: <PaletteIcon /> },
  { key: 'nft', label: 'NFTs', icon: <DiamondIcon /> },
  { key: 'professional', label: 'Services', icon: <WorkIcon /> },
  { key: 'charity', label: 'Charity', icon: <VolunteerActivismIcon /> },
  { key: 'specialty', label: 'Specialty', icon: <StorefrontIcon /> },
];

/**
 * Finds a product's category from the typicalProducts array
 * based on user input (synonyms). If found, returns that category;
 * otherwise returns null.
 */
const findCategoryForQuery = (q) => {
  const lowerQuery = q.toLowerCase();
  const matchingProducts = typicalProducts.filter(item =>
    item.product.toLowerCase().includes(lowerQuery) ||
    lowerQuery.includes(item.product.toLowerCase())
  );
  if (matchingProducts.length > 0) {
    return matchingProducts[0].category;
  }
  return null;
};

// How many category chips show before the "MORE" toggle expands the grid
const COLLAPSED_CAT_COUNT = 11;

// Chrome/Edge/Samsung Internet expose SpeechRecognition (webkit-prefixed);
// Firefox doesn't yet — the mic button hides itself there.
const SpeechRecognitionImpl =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

const CryptoPurchaseFinder = ({ godMode = false }) => {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [showAllCats, setShowAllCats] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  // Shared by typing and voice: set the query and auto-open the
  // best-matching category (product synonyms first, then a category key
  // mentioned directly in the query).
  const applyQuery = (value) => {
    setQuery(value);

    if (!value.trim()) {
      setActiveCategory(null);
      return;
    }

    const category = findCategoryForQuery(value);
    if (category && suggestionsMapping[category]) {
      setActiveCategory(category);
      return;
    }
    const lowerVal = value.toLowerCase();
    const keyMatch = Object.keys(suggestionsMapping).find((key) =>
      lowerVal.includes(key.toLowerCase())
    );
    setActiveCategory(keyMatch || null);
  };

  const handleInputChange = (e) => applyQuery(e.target.value);

  const handleVoiceSearch = () => {
    if (!SpeechRecognitionImpl) return;

    // Tap again while listening = cancel
    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }

    const recognition = new SpeechRecognitionImpl();
    recognitionRef.current = recognition;
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const transcript = event.results[0]?.[0]?.transcript || '';
      if (transcript) applyQuery(transcript);
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    setIsListening(true);
    try {
      recognition.start();
    } catch (e) {
      setIsListening(false);
    }
  };

  // Stop listening if the widget unmounts mid-recognition
  useEffect(() => () => recognitionRef.current?.abort?.(), []);

  const handleChipClick = (key) => {
    // Tapping the active chip closes it; tapping another switches to it
    setActiveCategory((prev) => (prev === key ? null : key));
  };

  // Categories shown in the browse grid: all of them when idle, narrowed
  // while typing (by label, key, or any product synonym in that category).
  const lowerQuery = query.trim().toLowerCase();
  const filteredCats = !lowerQuery
    ? CATEGORY_META
    : CATEGORY_META.filter(({ key, label }) => {
        if (label.toLowerCase().includes(lowerQuery)) return true;
        if (key.toLowerCase().includes(lowerQuery)) return true;
        return typicalProducts.some(
          (item) =>
            item.category === key &&
            (item.product.toLowerCase().includes(lowerQuery) ||
              lowerQuery.includes(item.product.toLowerCase()))
        );
      });

  // Idle view collapses to two rows of chips until "MORE" is tapped;
  // while searching, every matching category is shown.
  const visibleCats =
    !lowerQuery && !showAllCats
      ? filteredCats.slice(0, COLLAPSED_CAT_COUNT)
      : filteredCats;
  const hiddenCount = filteredCats.length - visibleCats.length;

  const stores = activeCategory ? suggestionsMapping[activeCategory] || [] : [];
  const activeMeta = CATEGORY_META.find((c) => c.key === activeCategory);

  const getStoreInitial = (label = '') => {
    const trimmed = label.trim();
    return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
  };

  const chipSx = (selected) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '8px 2px',
    borderRadius: '10px',
    cursor: 'pointer',
    userSelect: 'none',
    backgroundColor: selected ? 'rgba(153,50,204,0.9)' : 'rgba(186,85,211,0.3)',
    border: selected ? '2px solid #FFFFFF' : '2px solid rgba(255,255,255,0.25)',
    boxShadow: selected ? '0 0 14px rgba(186,85,211,0.9)' : 'none',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(153,50,204,0.7)',
      transform: 'translateY(-2px)',
    },
    '& svg': { fontSize: 26, color: '#FFFFFF' },
  });

  return (
    <Box
      sx={{
        position: 'relative',
        margin: '20px auto',
        width: '100%',
        maxWidth: '420px',
      }}
    >
      {/* Stationary god circle layer: stays put while the box is dragged
          aside, so the viewer can see the image unobstructed. */}
      {godMode && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(https://media.satoshiadvance.com/images/GodCircleThree.png)',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            backgroundSize: 'contain',
            borderRadius: '10px',
            pointerEvents: 'none',
          }}
        />
      )}
    <AnimatedGlowBox
      godMode={godMode}
      style={{ margin: 0 }}
      // Click-and-drag left/right (same feel as the other purple boxes)
      drag="x"
      dragMomentum={false}
      dragElastic={0.15}
      dragConstraints={{ left: -600, right: 600 }}
      whileDrag={{ cursor: 'grabbing' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, boxShadow: '0 0 20px #9932CC, 0 0 30px #9932CC, 0 0 40px #9932CC' }}
      whileHover={{ boxShadow: '0 0 40px #BA55D3, 0 0 60px #BA55D3, 0 0 80px #BA55D3' }}
      transition={{ duration: 1.5, ease: 'easeInOut' }}
    >
      <TitleText>Shop with Bitcoin & Crypto Online</TitleText>
      <RetroTextField
        value={query}
        onChange={handleInputChange}
        placeholder={isListening ? 'Listening...' : 'Type or speak a product, or tap a category'}
        variant="outlined"
        fullWidth
        InputProps={{
          endAdornment: SpeechRecognitionImpl ? (
            <InputAdornment position="end">
              <IconButton
                onClick={handleVoiceSearch}
                aria-label={isListening ? 'Stop listening' : 'Search by voice'}
                sx={{
                  color: isListening ? '#FF5252' : '#FFFFFF',
                  animation: isListening ? 'micPulse 1s ease-in-out infinite' : 'none',
                  '@keyframes micPulse': {
                    '0%, 100%': { filter: 'drop-shadow(0 0 2px #FF5252)', transform: 'scale(1)' },
                    '50%': { filter: 'drop-shadow(0 0 12px #FF5252)', transform: 'scale(1.15)' },
                  },
                }}
              >
                <MicIcon />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />

      {/* Browse grid: every category as a tappable icon chip */}
      {filteredCats.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 1,
            mb: 2,
          }}
        >
          {visibleCats.map(({ key, label, icon }) => (
            <Box key={key} onClick={() => handleChipClick(key)} sx={chipSx(activeCategory === key)}>
              {icon}
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '0.42rem',
                  textAlign: 'center',
                  lineHeight: 1.3,
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
          {hiddenCount > 0 && (
            <Box onClick={() => setShowAllCats(true)} sx={chipSx(false)}>
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '0.85rem',
                  lineHeight: 1,
                }}
              >
                +{hiddenCount}
              </Typography>
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '0.42rem',
                }}
              >
                MORE
              </Typography>
            </Box>
          )}
          {!lowerQuery && showAllCats && (
            <Box onClick={() => setShowAllCats(false)} sx={chipSx(false)}>
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '0.85rem',
                  lineHeight: 1,
                }}
              >
                −
              </Typography>
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '0.42rem',
                }}
              >
                LESS
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Typography
          sx={{
            color: '#FFFFFF',
            fontFamily: "'Press Start 2P', cursive",
            fontSize: '0.6rem',
            mb: 2,
            opacity: 0.85,
            lineHeight: 1.6,
          }}
        >
          No category found — try Food, Tires, Games, Flights...
        </Typography>
      )}

      {/* Every store in the selected category */}
      {activeMeta && stores.length > 0 && (
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "'Press Start 2P', cursive",
              color: '#FFFFFF',
              fontSize: '0.8rem',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              textShadow: `
                -1px -1px 0 #000,
                1px -1px 0 #000,
                -1px 1px 0 #000,
                1px 1px 0 #000
              `,
            }}
          >
            {React.cloneElement(activeMeta.icon, { sx: { fontSize: 22, color: '#FFFFFF' } })}
            {activeMeta.label} — {stores.length} stores:
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))',
              gap: 1.5,
              justifyItems: 'center',
              alignItems: 'stretch',
            }}
          >
            {stores.map((suggestion, index) => {
              const domain = new URL(suggestion.url).hostname;
              const initial = getStoreInitial(suggestion.label);

              return (
                <Tooltip
                  key={index}
                  title={domain}
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '1.2rem',
                        whiteSpace: 'normal',
                        maxWidth: 260,
                      },
                    },
                  }}
                >
                  <Button
                    href={suggestion.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    sx={{
                      width: '100%',
                      minHeight: 104,
                      minWidth: 0,
                      backgroundColor: 'rgba(186,85,211,0.75)',
                      fontFamily: "'Press Start 2P', cursive",
                      textTransform: 'none',
                      boxShadow: '0 0 12px rgba(153,50,204,0.9)',
                      borderRadius: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      p: '8px 4px',
                      '&:hover': {
                        backgroundColor: 'rgba(153,50,204,0.85)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        color: '#FFFFFF',
                        fontSize: '2rem',
                        lineHeight: 1,
                        letterSpacing: '2px',
                      }}
                    >
                      {initial}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#fff',
                        fontSize: '0.55rem',
                        textAlign: 'center',
                        px: 0.5,
                        lineHeight: 1.3,
                        wordBreak: 'break-word',
                      }}
                    >
                      {suggestion.label}
                    </Typography>
                  </Button>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}
    </AnimatedGlowBox>
    </Box>
  );
};

export default CryptoPurchaseFinder;
